"use client";

import { useIsPCPlatform } from "@mgdx/shared/src/hooks/useIsPCPlatform";
import { CameraIcon, DeleteIcon, FileImageIcon, SendIcon } from "@mgdx/ui/components/Icon";
import { IconButton } from "@mgdx/ui/components/IconButton";
import { Textarea } from "@mgdx/ui/components/Textarea";
import clsx from "clsx";
import { Field, FieldProps, Form, Formik } from "formik";
import { type ReactNode, useCallback, useRef, useState } from "react";
import { flushSync } from "react-dom";

import styles from "./index.module.css";
import { InputFile } from "./InputFile";
import { readFile } from "./readFile";

export type TalkFormValues = {
  message: string;
  file: File | undefined;
};

const initialValues: TalkFormValues = {
  message: "",
  file: undefined,
};

export type TalkFormProps = {
  onSubmit: (values: TalkFormValues) => void | Promise<void>;
};

export const TalkForm = ({ onSubmit }: TalkFormProps) => {
  const [isEntering, setIsEntering] = useState(false);
  const [imageSrc, setImageSrc] = useState("");
  const [key, setKey] = useState(0);
  const isPCPlatform = useIsPCPlatform();
  const resetInputFile = useCallback(() => {
    setKey((x) => x + 1);
  }, []);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  return (
    <Formik<TalkFormValues>
      initialValues={initialValues}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        setSubmitting(true);

        try {
          await onSubmit(values);

          resetForm();
          resetInputFile();
          // 入力に合わせて広げた高さを、作り直していたときと同じ初期の高さに戻す
          messageRef.current?.style.removeProperty("height");
        } finally {
          // 送信中は入力欄が disabled でフォーカスできないため、解除を DOM に反映してから戻す
          flushSync(() => setSubmitting(false));
          messageRef.current?.focus();
        }
      }}
    >
      {({ isSubmitting, values, setFieldValue }) => (
        <Form className={styles.form} noValidate={true}>
          <div className={clsx(styles.fields, isEntering && styles.isEntering, values.file && styles.hasFile)}>
            <div className={clsx(styles.cameraButton, isPCPlatform && styles.hidden)}>
              <Field name="file">
                {({ field: { onChange, value, ...field }, meta, form }: FieldProps) => (
                  <InputFile
                    {...field}
                    key={`camera-${key}`}
                    accept={{ "image/*": [] }}
                    capture="environment"
                    icon={<CameraIcon />}
                    title="写真を撮る"
                    aria-label="写真を撮る"
                    disabled={isSubmitting}
                    onChangeFile={async (file: File) => {
                      setImageSrc(await readFile(file));
                      setFieldValue("file", file);
                    }}
                  />
                )}
              </Field>
            </div>

            <div className={styles.pictureButton}>
              <Field name="file">
                {({ field: { onChange, value, ...field }, meta, form }: FieldProps) => (
                  <InputFile
                    {...field}
                    key={`picture-${key}`}
                    accept={{ "image/*": [] }}
                    icon={<FileImageIcon />}
                    title="写真を添付する"
                    aria-label="写真を添付する"
                    disabled={isSubmitting}
                    onChangeFile={async (file: File) => {
                      setImageSrc(await readFile(file));
                      setFieldValue("file", file);
                    }}
                  />
                )}
              </Field>
            </div>

            <div className={styles.previewFile}>
              {imageSrc && <img src={imageSrc} alt="画像" className={styles.previewImage} />}
              <IconButton
                aria-label="削除する"
                variant="filled"
                size="s"
                onClick={() => {
                  setImageSrc("");
                  setFieldValue("file", undefined);
                  resetInputFile();
                }}
              >
                <DeleteIcon />
              </IconButton>
            </div>

            <div className={styles.inputMessage}>
              <Field name="message">
                {({ field }: FieldProps): ReactNode => (
                  <Textarea
                    {...field}
                    ref={messageRef}
                    aria-label="メッセージ"
                    disabled={isSubmitting}
                    placeholder="メッセージ入力"
                    variant="field-only"
                    rows={1}
                    onFocus={() => {
                      setIsEntering(true);
                    }}
                    onBlur={() => setIsEntering(false)}
                  />
                )}
              </Field>
            </div>

            <IconButton
              type="submit"
              aria-label="送信する"
              variant="subtle"
              color="secondary"
              size="s"
              disabled={isSubmitting || (!values.file && !values.message)}
            >
              <SendIcon />
            </IconButton>
          </div>
        </Form>
      )}
    </Formik>
  );
};
